import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { MagneticButton } from './ui/MagneticButton';
import { Reveal } from './ui/Reveal';
import type { Project } from '../data/site';
import { links } from '../data/site';

/** Full case-study panel for a real project. Opened from the portfolio grid. */
export function ProjectDetail({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <motion.section ref={ref} className="border-y border-line bg-surface2 py-16 sm:py-24" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} aria-label={`${project.title} case study`} data-id="element-451" data-mp="8okjn9vm7s21">
      <div className="mx-auto w-full max-w-container px-5 sm:px-8" data-id="element-452" data-mp="3833e37lhx30v">
        <div className="flex items-start justify-between gap-6" data-id="element-453" data-mp="382afwv0lrluc">
          <div data-id="element-454" data-mp="2u43sy9x6z69g">
            <p className="label text-brand" data-id="element-455" data-mp="2u2ivocjht97w">Case study · {project.year}</p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.9] tracking-tightest sm:text-6xl" data-id="element-456" data-mp="2z1m323my3rj1">
              {project.title}
            </h2>
            <p className="mt-3 text-base text-muted" data-id="element-457" data-mp="2p3xw8fc2etki">{project.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close case study" className="flex h-10 w-10 shrink-0 items-center justify-center border border-line text-ink transition-colors hover:border-brand hover:text-brand" data-id="element-458" data-mp="2bct4s0iup7l9">
            <XIcon className="h-4 w-4" data-id="element-459" data-mp="21gzhpn7i9vp8" />
          </button>
        </div>

        <div className="mt-10 grid gap-10 border-t border-line pt-8 lg:grid-cols-[1.2fr_0.8fr]" data-id="element-460" data-mp="26d6lb9cfiv40">
          <div className="max-w-2xl space-y-5" data-id="element-461" data-mp="bz2o1sxkh3if">
            <p className="text-base leading-relaxed text-muted sm:text-lg" data-id="element-462" data-mp="7719u1gu92pr">{project.intro}</p>
            {project.description ? <p className="text-base leading-relaxed text-muted" data-id="element-463" data-mp="35zalkmx8qns">{project.description}</p> : null}
          </div>
          <ul className="space-y-2" data-id="element-464" data-mp="3udbqlwc2iy24">
            {project.scope.map(item => <li key={item} className="flex items-center gap-3 text-sm" data-id="element-465" data-mp="uvu0pjilskdi">
                <span className="h-1 w-1 rotate-45 bg-brand" aria-hidden="true" data-id="element-466" data-mp="quig5sel6pc6" />
                {item}
              </li>)}
          </ul>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2" data-id="element-467" data-mp="gwkvfevxraob">
          {project.gallery.map((item, index) => <Reveal key={item.src} delay={index * 0.06} className={item.span === 'full' ? 'md:col-span-2' : ''} data-id="element-468" data-mp="36lmyoh35sblp" data-mpi={index}>
              <figure className="group" data-id="element-469" data-mp="31owi7snfzdzr">
                <div className="overflow-hidden bg-white" data-id="element-470" data-mp="2rjo5ll4mpt43">
                  <img src={item.src} alt={item.caption} className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" decoding="async" data-id="element-471" data-mp="2rh1yfmlnhfme" />
                </div>
                <figcaption className="label mt-3 text-muted" data-id="element-472" data-mp="3a9mnsu2552lg">
                  {String(index + 1).padStart(2, '0')} — {item.caption}
                </figcaption>
              </figure>
            </Reveal>)}
        </div>

        {project.services || project.deliverables ? <div className="mt-12 grid gap-10 border-t border-line pt-8 sm:grid-cols-2" data-id="element-473" data-mp="3p6n5e4qmbh67">
            {project.services ? <Reveal data-id="element-474" data-mp="1pu2j1f0ttabn">
                <p className="label text-brand" data-id="element-475" data-mp="1puugywhwbk53">Services</p>
                <ul className="mt-4 space-y-2" data-id="element-476" data-mp="1uscgjwa0wn0f">
                  {project.services.map(item => <li key={item} className="flex items-center gap-3 text-sm" data-id="element-477" data-mp="3l9hgxvmtthe7">
                      <span className="h-1 w-1 rotate-45 bg-brand" aria-hidden="true" data-id="element-478" data-mp="3lbkd6xeko6rd" />
                      {item}
                    </li>)}
                </ul>
              </Reveal> : null}
            {project.deliverables ? <Reveal delay={0.08} data-id="element-479" data-mp="3pbulwhtr1b9x">
                <p className="label text-brand" data-id="element-480" data-mp="31lqh474tr5hc">Deliverables</p>
                <ul className="mt-4 space-y-2" data-id="element-481" data-mp="31m9y9lmnhpub">
                  {project.deliverables.map(item => <li key={item} className="flex items-center gap-3 text-sm" data-id="element-482" data-mp="2xlzu8eif59i3">
                      <span className="h-1 w-1 rotate-45 bg-brand" aria-hidden="true" data-id="element-483" data-mp="3bcbook2h7kce" />
                      {item}
                    </li>)}
                </ul>
              </Reveal> : null}
          </div> : null}

        <div className="mt-12 flex flex-wrap gap-3 border-t border-line pt-8" data-id="element-484" data-mp="2dwfo5btpufdr">
          <MagneticButton to="/contact" cursorLabel="Let's talk" showArrow data-id="element-485" data-mp="2dubm6zfdnqg4">
            Start a Project Like This
          </MagneticButton>
          <MagneticButton href={links.portfolio} variant="outline" cursorLabel="Portfolio" data-id="element-486" data-mp="y53nmpgolzih">
            View Full Portfolio
          </MagneticButton>
        </div>
      </div>
    </motion.section>;
}